import axios from "axios";
import documentLogo from "../images/logo.png";

const APPLICATION_PRINT_PATHS = [
  /^\/profile\/?$/,
  /^\/edit-user\/\d+\/?$/,
  /^\/work-history\/\d+\/?$/,
  /^\/edit-workhistory\/\d+\/?$/,
  /^\/qualifications\/\d+\/?$/,
  /^\/signed-release\/\d+(?:\/\d+)?\/?$/,
];

const TASKS = [
  { id: 18, key: "user_information", title: "Pg. 1 - Personal Information" },
  { id: 19, key: "employment_history", title: "Pg. 2 - Work and School History" },
  { id: 20, key: "qualifications", title: "Pg. 3 - Qualifications" },
  { id: 21, key: "signed_release", title: "Pg. 4 - Signed Release" },
];

const QUALIFICATION_LABELS = {
  dq_legal_right_work_us: "Legally entitled to work in the United States",
  dq_legal_right_us: "Legally entitled to work in the United States",
  dq_legal_right_enter_canada: "Legally entitled to enter Canada",
  dq_legal_right_canada: "Legally entitled to enter Canada",
  dq_fmcsa_registered: "Registered with the FMCSA Clearinghouse",
  dq_lift_40lb: "Able to lift and carry 40 lb",
  dq_previous_nwt_employee: "Previously employed by New Way Transport",
  dq_previous_nwt_from: "Previous employment from",
  dq_previous_nwt_to: "Previous employment to",
  dq_conviction: "Criminal conviction",
  dq_conviction_date: "Conviction date",
  dq_physical_condition: "Physical condition affecting job duties",
  dq_physical_condition_detail: "Physical condition details",
  dq_fast_card: "FAST card holder",
  dq_fast_card_no: "FAST card number",
  dq_fast_card_expiry: "FAST card expiry",
  dq_fast_card_revoked: "FAST card revoked",
  dq_fast_card_revoked_date: "FAST card revocation date",
  dq_accidents_last5yr: "Accidents during the last five years",
  dq_accidents_last5yr_date: "Accident date",
  dq_accidents_last5yr_detail: "Accident details",
  dq_violations_last3yr: "Traffic violations during the last three years",
  dq_violations_last3yr_date: "Violation date",
  dq_violations_last3yr_detail: "Violation details",
  dq_training: "Transportation-related training",
  dq_trainings: "Training details",
  dq_safe_driving_awards: "Safe-driving awards",
  dq_awards: "Award details",
  dq_license_denied: "Licence application denied",
  dq_denied_explanation: "Licence denial details",
  dq_abstract_clean: "Driver abstract is clean",
  dq_abstract_explanation: "Driver abstract details",
  dq_license_suspended: "Licence suspended or revoked",
  dq_suspended_explanation: "Suspension or revocation details",
  dq_commercial_license_date: "Commercial licence since",
  dq_education: "Highest education",
  dq_school: "School",
  dq_experience: "Driving experience",
  dq_exp_detail: "Driving experience details",
  dq_reg_travelled: "Regions travelled",
  dq_equip_operated: "Equipment operated",
  dq_equip_operated_detail: "Equipment details",
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const isPresent = (value) => {
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
};

const humanize = (value) =>
  String(value || "")
    .replace(/^dq_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatValue = (value) => {
  if (!isPresent(value)) return "—";
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === "object" ? Object.values(item).filter(isPresent).join(" — ") : item,
      )
      .filter(isPresent)
      .join(", ");
  }
  if (typeof value === "object") {
    return Object.entries(value)
      .filter(([, item]) => isPresent(item))
      .map(([key, item]) => `${humanize(key)}: ${formatValue(item)}`)
      .join("; ");
  }
  if (/^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(String(value))) {
    return formatDate(value);
  }
  return String(value);
};

const field = (label, value, wide = false) =>
  isPresent(value)
    ? `<div class="field${wide ? " field-wide" : ""}">
        <div class="field-label">${escapeHtml(label)}</div>
        <div class="field-value">${escapeHtml(formatValue(value))}</div>
      </div>`
    : "";

const getTaskValue = (map, task) => {
  const direct = map?.[String(task.id)] ?? map?.[task.key];
  if (isPresent(direct)) return direct;
  const keys = Object.keys(map || {}).filter(
    (key) => key.startsWith(`${task.id}_`) || key.startsWith(`${task.key}_`),
  );
  return keys.length ? map[keys[keys.length - 1]] : null;
};

const signatureBlock = (task, taskData) => {
  const signature =
    getTaskValue(taskData.signatures, task) ||
    taskData.accountSignature ||
    taskData.latestSignature;
  const signedDate = getTaskValue(taskData.signdates, task);
  const status = getTaskValue(taskData.status, task) || "not_completed";
  const signed = ["review", "completed", "almost_expired"].includes(
    String(status).toLowerCase(),
  );
  const safeSignature =
    typeof signature === "string" && signature.startsWith("data:image/")
      ? signature
      : null;

  return `<div class="signature-block">
    <div>
      <div class="signature-label">Employee signature</div>
      <div class="signature-frame">
        ${
          safeSignature
            ? `<img src="${escapeHtml(safeSignature)}" alt="Employee signature" />`
            : `<span>${signed ? "Signature unavailable" : "Not signed"}</span>`
        }
      </div>
    </div>
    <div class="signature-meta">
      <div><strong>Status:</strong> ${escapeHtml(signed ? "Signed" : "Not signed")}</div>
      <div><strong>Date:</strong> ${escapeHtml(signedDate ? formatDate(signedDate) : "—")}</div>
    </div>
  </div>`;
};

const companyHeader = (title) => `
  <header class="document-header">
    <img src="${escapeHtml(
      new URL(documentLogo, window.location.origin).href,
    )}" alt="New Way Transport" />
    <div><h1>${escapeHtml(title)}</h1><p>Employment Application</p></div>
    <address><strong>New Way Transport Inc.</strong><br />18337 Stony Island Ave<br />Lansing, IL 60438-1540</address>
  </header>`;

const personalSection = (user, taskData) => {
  let referral = user.refer_by;
  try {
    referral = typeof referral === "string" ? JSON.parse(referral) : referral;
  } catch {
    // Keep legacy text.
  }
  const fullName = [user.first_name, user.mid_name, user.last_name, user.suffix]
    .filter(isPresent)
    .join(" ");
  const address = [user.address1, user.address2, user.city, user.province, user.postal_code, user.country]
    .filter(isPresent)
    .join(", ");

  return `<section class="packet-section">
    ${companyHeader(TASKS[0].title)}
    <div class="section-title">Applicant details</div>
    <div class="field-grid">
      ${field("Employee ID", user.userid_no || user.userid || user.id)}
      ${field("Full name", fullName)}
      ${field("Preferred name", user.preferred_name)}
      ${field("Gender", user.gender)}
      ${field("Date of birth", user.dob)}
      ${field("Email", user.email)}
      ${field("Mobile", user.mobile)}
      ${field("Home phone", user.home_phone)}
      ${field("Position", user.position)}
      ${field("Employment category", user.emp_category)}
      ${field("Status", user.status)}
      ${field("Address", address, true)}
      ${field("Referred by", referral, true)}
      ${field("Physical condition", user.user_physical_condition, true)}
    </div>
    ${signatureBlock(TASKS[0], taskData)}
  </section>`;
};

const workHistoryCard = (record, index) => {
  const dateRange = `${formatDate(record.wh_datefrom) || "—"} to ${
    record.wh_dateto ? formatDate(record.wh_dateto) : "Present"
  }`;
  const contact = [
    record.wh_confname,
    record.wh_conlname,
    record.wh_contact_position,
  ]
    .filter(isPresent)
    .join(" ");
  const address = [
    record.wh_address1,
    record.wh_address2,
    record.wh_city,
    record.wh_province,
    record.wh_postcode,
    record.wh_country,
  ]
    .filter(isPresent)
    .join(", ");

  return `<article class="history-card">
    <h3>${index + 1}. ${escapeHtml(record.wh_name || record.wh_type || "History record")}</h3>
    <div class="mini-grid">
      ${field("Type", record.wh_type)}
      ${field("Dates", dateRange)}
      ${field("Position / program", record.wh_position || record.wh_skills)}
      ${field("Reason for leaving", record.wh_reason)}
      ${field("Contact", contact)}
      ${field("Phone", record.wh_phone)}
      ${field("Email", record.wh_email)}
      ${field("Address", address, true)}
      ${field("Terminated", record.wh_terminated)}
      ${field("Commercial vehicle", record.wh_comm_vehicle)}
      ${field("Vehicle / equipment types", record.wh_comm_vehicle_types, true)}
    </div>
  </article>`;
};

const workHistorySection = (records, taskData) => `<section class="packet-section">
  ${companyHeader(TASKS[1].title)}
  <div class="section-title">Employment, education and activity history</div>
  ${
    records.length
      ? `<div class="history-grid">${records.map(workHistoryCard).join("")}</div>`
      : '<p class="empty-note">No work or school history was provided.</p>'
  }
  ${signatureBlock(TASKS[1], taskData)}
</section>`;

const qualificationsSection = (qualificationData, taskData) => {
  const questions = qualificationData?.driverQuestions || {};
  const entries = Object.entries(questions).filter(
    ([key, value]) =>
      key.startsWith("dq_") &&
      !["dq_id", "dq_userid", "dq_created_at", "dq_updated_at"].includes(key) &&
      isPresent(value),
  );

  return `<section class="packet-section">
    ${companyHeader(TASKS[2].title)}
    <div class="section-title">Qualifications and driver information</div>
    <div class="qualification-grid">
      ${entries
        .map(([key, value]) =>
          field(QUALIFICATION_LABELS[key] || humanize(key), value, typeof value === "object"),
        )
        .join("")}
    </div>
    ${entries.length ? "" : '<p class="empty-note">No qualification information was provided.</p>'}
    ${signatureBlock(TASKS[2], taskData)}
  </section>`;
};

const consentRow = (label, status, date) => {
  const signed = ["review", "completed", "almost_expired"].includes(
    String(status || "").toLowerCase(),
  );
  return `<tr><td>${escapeHtml(label)}</td><td>${signed ? "Signed" : "Not signed"}</td><td>${
    date ? escapeHtml(formatDate(date)) : "—"
  }</td></tr>`;
};

const signedReleaseSection = (user, records, taskData) => {
  const consents = [
    ["Consent to Background Checks", "backgroundcheck", 23],
    ["Summary of Rights Under FCRA", "summary_of_rights_under_fcra", 55],
    ["PSP Disclosure & Authorization", "psp_disclosure_and_authorization", 78],
    ["Background Check Authorization & Disclosure (FCRA)", "background_check_fcra", 58],
  ]
    .filter(([, key, id], index) => index === 0 || isPresent(taskData.status?.[key]) || isPresent(taskData.status?.[String(id)]))
    .map(([label, key, id]) => ({
      label,
      status: taskData.status?.[String(id)] ?? taskData.status?.[key],
      date: taskData.signdates?.[String(id)] ?? taskData.signdates?.[key],
    }));

  records
    .filter((record) => record.wh_type === "Working")
    .forEach((record) => {
      const key = `consent_history_${record.wh_id}`;
      consents.push({
        label: `Consent to Safety and Performance History — ${record.wh_name || "Employer"}`,
        status: taskData.status?.[key],
        date: taskData.signdates?.[key],
      });
    });

  return `<section class="packet-section">
    ${companyHeader(TASKS[3].title)}
    <div class="applicant-line"><strong>Applicant:</strong> ${escapeHtml(
      [user.first_name, user.last_name].filter(isPresent).join(" "),
    )}</div>
    <div class="section-title">Consent document status</div>
    <table><thead><tr><th>Document</th><th>Status</th><th>Date</th></tr></thead>
      <tbody>${consents.map((item) => consentRow(item.label, item.status, item.date)).join("")}</tbody>
    </table>
    <div class="acknowledgement">
      <strong>Employment Application Disclaimer and Acknowledgement</strong>
      <p>I certify that this application was completed by me and that all entries and information are true and complete to the best of my knowledge. I understand that any misstatement or omission may result in dismissal.</p>
    </div>
    ${signatureBlock(TASKS[3], taskData)}
  </section>`;
};

const documentStyles = `
  @page { size: letter portrait; margin: 9mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #172033; font-family: Arial, Helvetica, sans-serif; }
  body { font-size: 9.25pt; line-height: 1.28; }
  .packet-section { break-before: page; page-break-before: always; width: 100%; }
  .packet-section:first-child { break-before: auto; page-break-before: auto; }
  .document-header { display: grid; grid-template-columns: 36mm 1fr 42mm; align-items: center; gap: 5mm; padding-bottom: 3mm; border-bottom: 1.5px solid #176b71; margin-bottom: 4mm; }
  .document-header img { width: 31mm; max-height: 16mm; object-fit: contain; }
  .document-header h1 { font-size: 16pt; line-height: 1.05; margin: 0; text-align: center; color: #183b56; }
  .document-header p { margin: 1mm 0 0; text-align: center; color: #536273; }
  .document-header address { margin: 0; text-align: right; font-style: normal; font-size: 7.5pt; line-height: 1.3; }
  .section-title { font-weight: 700; font-size: 10.5pt; color: #176b71; margin: 0 0 2.5mm; text-transform: uppercase; letter-spacing: .35px; }
  .field-grid, .qualification-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2mm 3mm; }
  .qualification-grid { grid-template-columns: repeat(2, 1fr); }
  .field, .history-card { break-inside: avoid; page-break-inside: avoid; }
  .field { min-width: 0; border-bottom: .5px solid #c9d3dc; padding: 1.2mm 1mm 1.5mm; }
  .field-wide { grid-column: 1 / -1; }
  .field-label { font-size: 7pt; text-transform: uppercase; color: #64748b; font-weight: 700; letter-spacing: .2px; }
  .field-value { margin-top: .5mm; min-height: 3.2mm; overflow-wrap: anywhere; }
  .history-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 3mm; }
  .history-card { border: .7px solid #aebdca; border-radius: 2mm; padding: 2.5mm; }
  .history-card h3 { color: #183b56; font-size: 10pt; margin: 0 0 1.5mm; border-bottom: .5px solid #d7e0e7; padding-bottom: 1mm; }
  .mini-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1mm 2mm; }
  .mini-grid .field { padding: .7mm .5mm 1mm; }
  .signature-block { display: grid; grid-template-columns: 1fr 45mm; gap: 5mm; align-items: end; margin-top: 5mm; padding-top: 3mm; border-top: 1px solid #8797a6; break-inside: avoid; page-break-inside: avoid; }
  .signature-label { font-weight: 700; margin-bottom: 1mm; }
  .signature-frame { height: 18mm; width: 65mm; border-bottom: 1px solid #172033; display: flex; align-items: center; color: #718096; }
  .signature-frame img { max-width: 62mm; max-height: 16mm; object-fit: contain; object-position: left center; }
  .signature-meta { font-size: 8pt; line-height: 1.7; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 4mm; }
  th, td { border: .5px solid #aebdca; padding: 2mm; text-align: left; }
  th { background: #eef5f5; color: #183b56; font-size: 8pt; }
  th:nth-child(2), th:nth-child(3), td:nth-child(2), td:nth-child(3) { width: 25mm; }
  .acknowledgement { border: .7px solid #aebdca; padding: 3mm; break-inside: avoid; page-break-inside: avoid; }
  .acknowledgement p { margin: 1.5mm 0 0; }
  .applicant-line { margin-bottom: 3mm; }
  .empty-note { color: #64748b; font-style: italic; }
  @media screen { body { max-width: 216mm; margin: 0 auto; padding: 9mm; } }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;

const waitForImages = (documentNode) =>
  Promise.all(
    Array.from(documentNode.images).map((image) => {
      if (image.complete) return Promise.resolve();
      return new Promise((resolve) => {
        image.onload = resolve;
        image.onerror = resolve;
      });
    }),
  );

export const isApplicationPacketPrintPath = (pathname = window.location.pathname) =>
  APPLICATION_PRINT_PATHS.some((pattern) => pattern.test(pathname));

export const resolveApplicationPacketUserId = (
  pathname = window.location.pathname,
) => {
  if (/^\/profile\/?$/.test(pathname)) return localStorage.getItem("userId");
  return pathname.match(/\/(\d+)(?:\/|$)/)?.[1] || localStorage.getItem("userId");
};

export const printApplicationPacket = async (userId) => {
  if (!userId) throw new Error("The employee could not be identified.");

  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const headers = {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  };
  const [userResponse, historyResponse, qualificationResponse, taskResponse] =
    await Promise.all([
      axios.get(`${baseUrl}/api/users/${userId}`, { headers }),
      axios.get(`${baseUrl}/api/work-history/${userId}`, { headers }),
      axios.get(`${baseUrl}/api/qualifications/${userId}`, { headers }),
      axios.get(`${baseUrl}/api/usertasks/status/${userId}`, { headers }),
    ]);

  const user = userResponse.data || {};
  const histories = Array.isArray(historyResponse.data) ? historyResponse.data : [];
  const qualifications = qualificationResponse.data || {};
  const taskData = taskResponse.data || {};
  const title = `Employment Application - ${
    [user.first_name, user.last_name].filter(isPresent).join(" ") || userId
  }`;
  const body = [
    personalSection(user, taskData),
    workHistorySection(histories, taskData),
    qualificationsSection(qualifications, taskData),
    signedReleaseSection(user, histories, taskData),
  ].join("");

  const printFrame = document.createElement("iframe");
  printFrame.title = `${title} print preview`;
  Object.assign(printFrame.style, {
    position: "fixed",
    width: "1px",
    height: "1px",
    right: "0",
    bottom: "0",
    border: "0",
    opacity: "0",
    pointerEvents: "none",
  });
  document.body.appendChild(printFrame);

  const printDocument = printFrame.contentDocument;
  printDocument.open();
  printDocument.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(
      title,
    )}</title><style>${documentStyles}</style></head><body>${body}</body></html>`,
  );
  printDocument.close();
  await printDocument.fonts?.ready;
  await waitForImages(printDocument);

  printFrame.contentWindow?.focus();
  printFrame.contentWindow?.print();
  window.setTimeout(() => printFrame.remove(), 120000);
};
